import { memo, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMediaQuery } from 'react-responsive';

import { CheckTypeInline } from '@/services/ws-operations/src/main/resources/rest-api';
import { CampaignPositionStatus } from '@/services/ws-print-operations/src/main/resources/rest-api';
import { useCallbackRef } from '@/js/libs/hooks/useCallbackRef';
import { Media } from '@/js/libs/ui';
import { Badge, Icons, PuzzleNav } from '@/js/libs/ui/src/V2';
import { PrivateContent, Roles, useIdentityRoles, and, or, not } from '@/js/libs/use-auth';

import { ROUTER } from 'src/AppRouter';
import { DEV_MODE, MOBILE_HEADER_HEIGHT } from 'src/constants';
import CompaniesSwitch from 'src/modules/CompaniesSwitch';
import { settingsRouteRoles } from 'src/routes/settings';
import { DigitalOperationStatus } from 'src/scenes/DigitalOperations';
import { Footer, Header } from './components';
import { SearchInput } from './components/Header/Search';
import { useNavigationContext } from './context';

export const Navigation = memo(function Navigation() {
  // Please use only common and components namespaces here to avoid excessive namespaces loading
  const [t] = useTranslation(['common', 'components']);

  const { SWITCH_COMPANY_VIEW, POSTNETZ_BRANCH_USER, POSTADMANAGER, SELF_BOOKING } = useIdentityRoles();
  const isDesktop = useMediaQuery({ query: Media.lgMin });

  const { isCollapsedNav, collapseMainNav, pendingArticlesCount, articlesCount } = useNavigationContext();

  const [needSearchAutofocus, setNeedSearchAutofocus] = useState(false);

  const openUserSnap = useCallbackRef(() => {
    window.Usersnap?.logEvent('open_usersnap');
  });

  useEffect(() => {
    if (isCollapsedNav) {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setNeedSearchAutofocus(false);
    }
  }, [isCollapsedNav]);

  return (
    <PuzzleNav
      header={isDesktop && <Header onNeedSearchAutofocus={setNeedSearchAutofocus} />}
      mobileHeaderHeight={MOBILE_HEADER_HEIGHT}
      searchInput={!POSTNETZ_BRANCH_USER && !POSTADMANAGER && !SELF_BOOKING && <SearchInput autofocus={needSearchAutofocus} />}
      companySelect={SWITCH_COMPANY_VIEW && <CompaniesSwitch />}
      footer={<Footer isCollapsedNav={isCollapsedNav} />}
      isCollapsedNav={isCollapsedNav}
      collapseMainNav={collapseMainNav}>
      <PrivateContent roles={and(or(Roles.MANAGE_ACCOUNTS, Roles.SELF_BOOKING), not(or(Roles.POSTADMANAGER, Roles.POSTNETZ_BRANCH_USER)))}>
        <PuzzleNav.Item icon={<Icons.Users />} title={t('components:CRM')} itemKey='crm' path='/crm' dataTestId='crm-category'>
          <PrivateContent roles={or(Roles.MANAGE_ACCOUNTS, Roles.SELF_BOOKING)}>
            <PuzzleNav.ItemSubLink title={t('common:ACCOUNTS')} to='/crm/accounts' dataTestId='accounts-link' />
          </PrivateContent>
          <PrivateContent roles={Roles.MANAGE_ACCOUNTS}>
            <PuzzleNav.ItemSubLink title={t('common:CONTACTS')} to='/crm/contacts' dataTestId='contacts-link' />
          </PrivateContent>
        </PuzzleNav.Item>
      </PrivateContent>

      <PrivateContent roles={Roles.POSTADMANAGER}>
        <PuzzleNav.Item icon={<Icons.Users />} title={t('components:CRM')} itemKey='crm' path='/crm' dataTestId='crm-category'>
          <PrivateContent roles={Roles.POSTADMANAGER}>
            <PuzzleNav.ItemSubLink title={t('common:ACCOUNTS')} to='/post-crm/accounts' dataTestId='accounts-link' />
          </PrivateContent>
          <PuzzleNav.ItemSubLink title={t('common:CONTACTS')} to='/post-crm/contacts' dataTestId='contacts-link' />
        </PuzzleNav.Item>
      </PrivateContent>

      <PrivateContent roles={Roles.SALESFUNNEL}>
        <PuzzleNav.Item dataTestId='sales-category' icon={<Icons.Megaphone />} title={t('common:SALES')} itemKey='sales' path='/sales'>
          <PuzzleNav.ItemSubLink
            title={t('common:TASKS')}
            to={ROUTER['/sales/tasks'].path({ STATUS: 'IN_PROGRESS' })}
            path={ROUTER['/sales/tasks'].path.pattern}
            dataTestId='tasks-link'
          />
          <PuzzleNav.ItemSubLink
            title={t('common:ACTIVITIES')}
            to={ROUTER['/sales/activities'].path.pattern}
            path={[ROUTER['/sales/activities'].path.pattern, ROUTER['/sales/comments'].path.pattern]}
            dataTestId='activities-link'
          />
          <PuzzleNav.ItemSubLink title={t('common:PROMOTIONS')} to='/sales/promotions' dataTestId='promotions-link' />
          {DEV_MODE && (
            <PuzzleNav.ItemSubLink
              isNew
              title={t('common:PROMOTIONS')}
              to={ROUTER['/sales/promotions-v2'].path.pattern}
              dataTestId='promotions-link-v2'
            />
          )}
          <PuzzleNav.ItemSubLink
            title={t('common:BUTTON_SETTINGS')}
            path={['/sales/tags', '/sales/sources', '/sales/task-topics']}
            to='/sales/tags'
            dataTestId='sales-settings-link'
          />
        </PuzzleNav.Item>
      </PrivateContent>

      <PrivateContent roles={and(or(Roles.AGENCY, Roles.POSTADMANAGER), not(Roles.POSTNETZ_BRANCH_USER))}>
        <PuzzleNav.Item icon={<Icons.Document />} title={t('common:ORDERS')} itemKey='orders' path='/orders' dataTestId='orders-category'>
          <PrivateContent roles={Roles.POSTADMANAGER}>
            <PuzzleNav.ItemSubLink title={t('common:POST_CAMPAIGNS')} to='/orders/post-campaigns' dataTestId='post-campaigns-link-orders' />
          </PrivateContent>
          <PrivateContent roles={not(Roles.POSTADMANAGER)}>
            <PuzzleNav.ItemSubLink title={t('common:TITLE_CAMPAIGNS_MAIN')} to='/orders/campaigns' dataTestId='campaigns-link-orders' />
            <PrivateContent
              roles={or(
                Roles.MANAGE_DIGITAL_PRODUCTS,
                Roles.BOOK_CAMPAIGNS,
                Roles.MANAGE_ACCOUNTS,
                Roles.SELF_BOOKING,
                Roles.VIEW_CAMPAIGNS,
              )}>
              <PuzzleNav.ItemSubLink
                title={t('common:DIGITAL_POSITIONS')}
                to='/orders/digital-positions'
                dataTestId='digital-positions-link-orders'
              />
              {DEV_MODE && (
                <PuzzleNav.ItemSubLink
                  isNew
                  title={t('common:DIGITAL_POSITIONS')}
                  to='/orders/digital-positions-v2'
                  dataTestId='digital-positions-link-orders-v2'
                />
              )}
            </PrivateContent>
            <PrivateContent roles={Roles.MANAGE_PRINT_PRODUCTS}>
              <PuzzleNav.ItemSubLink
                title={t('common:PRINT_POSITIONS')}
                to='/orders/print-positions'
                dataTestId='print-positions-link-orders'
              />
              {DEV_MODE && (
                <PuzzleNav.ItemSubLink
                  isNew
                  title={t('common:PRINT_POSITIONS')}
                  to='/orders/print-positions-v2'
                  dataTestId='print-positions-link-orders-v2'
                />
              )}
            </PrivateContent>
            <PrivateContent roles={Roles.MANAGE_RADIO_PRODUCTS}>
              <PuzzleNav.ItemSubLink
                title={t('common:RADIO_POSITIONS')}
                to='/orders/radio-positions'
                dataTestId='radio-positions-link-orders'
              />
              {DEV_MODE && (
                <PuzzleNav.ItemSubLink
                  isNew
                  title={t('common:RADIO_POSITIONS')}
                  to='/orders/radio-positions-v2'
                  dataTestId='radio-positions-link-orders-v2'
                />
              )}
            </PrivateContent>
            <PrivateContent roles={Roles.MANAGE_TV_PRODUCTS}>
              <PuzzleNav.ItemSubLink title={t('common:TV_POSITIONS')} to='/orders/tv-positions' dataTestId='tv-positions-link-orders' />
              {DEV_MODE && (
                <PuzzleNav.ItemSubLink
                  isNew
                  title={t('common:TV_POSITIONS')}
                  to='/orders/tv-positions-v2'
                  dataTestId='tv-positions-link-orders-v2'
                />
              )}
            </PrivateContent>
            <PrivateContent roles={Roles.MANAGE_POS_PRODUCTS}>
              <PuzzleNav.ItemSubLink
                title={t('common:POINT_OF_SALE_POSITIONS')}
                to={ROUTER['/orders/point-of-sale-positions'].path.pattern}
                dataTestId='point-of-sale-positions-link-orders'
              />
            </PrivateContent>
            <PrivateContent roles={Roles.ADMIN}>
              <PuzzleNav.ItemSubLink
                title={t('components:PROGRAMMATIC_DEALS')}
                to='/orders/programmatic-deals'
                dataTestId='programmatic-deals-link'
              />
            </PrivateContent>
            <PrivateContent roles={or(Roles.ADMIN, Roles.BOOK_CAMPAIGNS)}>
              <PuzzleNav.ItemSubLink title={`${t('common:BOOKING_TITLE')}`} to='/orders/booking' dataTestId='booking-calendar-link' />
            </PrivateContent>
            <PrivateContent roles={Roles.MANAGE_DIGITAL_PRODUCTS}>
              <PuzzleNav.ItemSubLink title={t('common:FORECAST')} to='/orders/forecast' dataTestId='forecast-link' />
            </PrivateContent>
          </PrivateContent>
        </PuzzleNav.Item>
      </PrivateContent>

      <PrivateContent roles={Roles.POSTNETZ_BRANCH_USER}>
        <PuzzleNav.Item icon={<Icons.Document />} title={t('common:ORDERS')} itemKey='orders' path='/orders' dataTestId='orders-category'>
          <PuzzleNav.ItemSubLink title={`${t('common:BOOKING_TITLE')}`} to='/orders/booking' dataTestId='booking-calendar-link' />
        </PuzzleNav.Item>
      </PrivateContent>

      <PrivateContent
        roles={or(Roles.SELF_BOOKING, Roles.ADSERVER_CREATIVES, Roles.MANAGE_DIGITAL_CREATIVES, Roles.MANAGE_PRINT_CREATIVES)}>
        <PuzzleNav.Item
          itemKey='creatives'
          icon={<Icons.Photo />}
          title={t('common:CREATIVES')}
          path='/creatives'
          dataTestId='creatives-category'>
          <PrivateContent roles={Roles.ADSERVER_CREATIVES}>
            <PuzzleNav.ItemSubLink
              title={t('common:ADSERVER_CREATIVES')}
              to='/creatives/ad-server'
              path='/creatives/ad-server'
              dataTestId='adserver-creatives-link'
            />
          </PrivateContent>
          <PrivateContent roles={or(Roles.MANAGE_DIGITAL_CREATIVES, Roles.SELF_BOOKING)}>
            <PuzzleNav.ItemSubLink
              title={t('common:DIGITAL_CREATIVES')}
              to='/creatives/digital'
              path='/creatives/digital'
              dataTestId='digital-creatives-link'
            />
          </PrivateContent>
          <PrivateContent roles={Roles.MANAGE_PRINT_CREATIVES}>
            <PuzzleNav.ItemSubLink
              title={t('common:PRINT_CREATIVES')}
              to='/creatives/print'
              path='/creatives/print'
              dataTestId='print-creatives-link'
            />
          </PrivateContent>
        </PuzzleNav.Item>
      </PrivateContent>

      <PrivateContent
        roles={or(and(Roles.MANAGE_DIGITAL_PRODUCTS, or(Roles.OPS_PUSH, Roles.OPS_SCREENSHOTS, Roles.OPS_CHECKS)), Roles.ADMIN)}>
        <PuzzleNav.Item
          icon={<Icons.Display />}
          title={t('common:OPERATIONS')}
          itemKey='operations'
          path='/digital-operations'
          dataTestId='digital-operations-category'>
          <PrivateContent roles={Roles.ADMIN}>
            <PuzzleNav.ItemSubLink
              title={t('components:PN_AD_APPROVAL')}
              to={`/digital-operations/approval/${DigitalOperationStatus.PENDING_APPROVAL}`}
              path='/digital-operations/approval'
              dataTestId='ad-approval-link'
              tag={!pendingArticlesCount && !!articlesCount ? <Badge informative xs title={articlesCount.toString()} /> : undefined}
            />
          </PrivateContent>
          <PrivateContent roles={Roles.OPS_PUSH}>
            <PuzzleNav.ItemSubLink
              title={t('components:PN_CAMPAIGN_MANAGEMENT')}
              to={`/digital-operations/campaign-management/${DigitalOperationStatus.READY_TO_PUSH}`}
              path='/digital-operations/campaign-management'
              dataTestId='campaign-management-link'
            />
          </PrivateContent>
          <PrivateContent roles={Roles.OPS_SCREENSHOTS}>
            <PuzzleNav.ItemSubLink
              title={t('components:PN_SCREENSHOT_MANAGEMENT')}
              to='/digital-operations/screenshot-management'
              dataTestId='screenshots-link'
            />
          </PrivateContent>
          <PrivateContent roles={Roles.OPS_CHECKS}>
            <PuzzleNav.ItemSubLink
              title={t('components:PN_QUALITY_ASSURANCE')}
              to={`/digital-operations/quality-assurance/${CheckTypeInline.START}`}
              path='/digital-operations/quality-assurance'
              dataTestId='quality-assurance-link'
            />
          </PrivateContent>
        </PuzzleNav.Item>
      </PrivateContent>

      <PrivateContent roles={or(Roles.POSTNETZ, Roles.POSTNETZ_BRANCH_USER)}>
        <PuzzleNav.Item
          icon={<Icons.Newspaper />}
          title={t('common:ANALOG_OPERATIONS')}
          itemKey='analog-operations'
          path='/analog-operations'
          dataTestId='analog-operations-category'>
          <PrivateContent roles={Roles.POSTNETZ}>
            <PuzzleNav.ItemSubLink
              title={t('components:PN_CAMPAIGN_MANAGEMENT')}
              to='/analog-operations/campaign-management'
              path='/analog-operations/campaign-management'
              dataTestId='analog-operations-campaign-management-link'
            />
          </PrivateContent>
          <PuzzleNav.ItemSubLink
            title={t('common:INVENTORY_MANAGEMENT')}
            to='/analog-operations/inventory-management'
            path='/analog-operations/inventory-management'
            dataTestId='analog-operations-inventory-management-link'
          />
        </PuzzleNav.Item>
      </PrivateContent>

      <PrivateContent roles={and(Roles.MANAGE_PRINT_PRODUCTS, Roles.PRINT_OPS_PUSH)}>
        <PuzzleNav.Item
          icon={<Icons.Newspaper />}
          title={t('components:PRINT_OPERATIONS')}
          itemKey='printoperations'
          path='/print-operations'
          dataTestId='print-operations-category'>
          <PuzzleNav.ItemSubLink
            title={t('common:PRINT')}
            to={`/print-operations/print/${CampaignPositionStatus.READY_TO_PUSH}`}
            path='/print-operations/print'
            dataTestId='print-link'
          />
          <PuzzleNav.ItemSubLink
            title={t('common:PUBLICATION_UPLOAD')}
            exact
            to='/print-operations/publication-upload'
            dataTestId='print-publication-upload-link'
          />
        </PuzzleNav.Item>
      </PrivateContent>
      {DEV_MODE && (
        <PrivateContent roles={and(Roles.MANAGE_PRINT_PRODUCTS, Roles.PRINT_OPS_PUSH)}>
          <PuzzleNav.Item
            isNew
            icon={<Icons.Newspaper />}
            title={t('components:PRINT_OPERATIONS')}
            itemKey='printoperationsnew'
            path='/print-operations-new'>
            <PuzzleNav.ItemSubLink
              title={t('common:PRINT')}
              to={ROUTER['/print-operations-new/print'].path({ activeTabId: CampaignPositionStatus.READY_TO_PUSH })}
              path={ROUTER['/print-operations-new/print'].path.pattern}
            />
            <PuzzleNav.ItemSubLink
              title={t('common:PUBLICATION_UPLOAD')}
              exact
              to='/print-operations/publication-upload'
              dataTestId='print-publication-upload-link'
            />
          </PuzzleNav.Item>
        </PrivateContent>
      )}
      <PrivateContent
        roles={and(
          or(Roles.ADMIN_INVOICE, Roles.ADMIN, Roles.PAYOUT, Roles.POSTADMANAGER, Roles.PAYMENTS),
          not(Roles.POSTNETZ_BRANCH_USER),
        )}>
        <PuzzleNav.Item
          icon={<Icons.Dollar />}
          title={t('components:FINANCES')}
          itemKey='finance'
          path='/finances'
          dataTestId='finances-category'>
          <PrivateContent roles={Roles.ADMIN_INVOICE}>
            <PuzzleNav.ItemSubLink title={t('common:INVOICES_TITLE')} to='/finances/invoices' dataTestId='invoicing-link' />
          </PrivateContent>
          <PrivateContent roles={Roles.POSTADMANAGER}>
            <PuzzleNav.ItemSubLink title={t('common:INVOICES_TITLE')} to='/finances/post-invoices' dataTestId='post-invoicing-link' />
          </PrivateContent>
          <PrivateContent roles={Roles.ADMIN}>
            <PuzzleNav.ItemSubLink
              title={t('common:COMPANY_EXPORTS_TITLE')}
              to='/finances/company-configs'
              dataTestId='company-configs-link'
            />
          </PrivateContent>
          <PrivateContent roles={Roles.PAYOUT}>
            <PuzzleNav.ItemSubLink
              title={t('common:PAYOUTS')}
              to='/finances/payouts/reports'
              path='/finances/payouts'
              dataTestId='payouts-link'
            />
          </PrivateContent>
          <PrivateContent roles={Roles.ADMIN}>
            <PuzzleNav.ItemSubLink
              title={t('common:RECEIPTS')}
              to='/finances/credit-card-receipts'
              dataTestId='credit-card-receipts-link'
            />
          </PrivateContent>
          <PrivateContent roles={Roles.PAYMENTS}>
            <PuzzleNav.ItemSubLink title={t('components:PAYMENTS_TITLE')} to='/finances/payments' dataTestId='payments-link' />
          </PrivateContent>
          <PrivateContent roles={Roles.ADMIN}>
            <PuzzleNav.ItemSubLink
              title={t('components:BOOKING_PERIODS_TITLE')}
              to='/finances/booking-periods'
              dataTestId='booking-periods-link'
            />
          </PrivateContent>
        </PuzzleNav.Item>
      </PrivateContent>

      <PrivateContent roles={or(Roles.ADMIN, and(Roles.REPORT_BUILDER, not(Roles.POSTNETZ_BRANCH_USER)))}>
        <PuzzleNav.Item
          icon={<Icons.Chart />}
          title={t('components:BUSINESS_INTELLIGENCE')}
          itemKey='business-intelligence'
          path='/business-intelligence'
          dataTestId='business-intelligence-category'>
          <PuzzleNav.ItemSubLink title={t('common:REPORTING')} to='/business-intelligence/reporting' dataTestId='reporting-link' />
        </PuzzleNav.Item>
      </PrivateContent>

      <PrivateContent roles={Roles.ADMIN}>
        <PuzzleNav.Item
          icon={<Icons.Chart />}
          title={t('components:ANALYTICS_TOOL')}
          itemKey='analytics-tool'
          path='/analytics-tool'
          dataTestId='analytics-tool-category'>
          <PuzzleNav.ItemSubLink
            title={t('components:ANALYTICS_TOOL_PAGE_IMPRESSIONS')}
            to='/analytics-tool/page-impressions'
            dataTestId='analytics-tool-page-impressions-link'
            isBeta
          />
          <PuzzleNav.ItemSubLink
            title={t('components:ANALYTICS_TOOL_AD_IMPRESSIONS')}
            to='/analytics-tool/ad-impressions'
            dataTestId='analytics-tool-ad-impressions-link'
            isBeta
          />
        </PuzzleNav.Item>
      </PrivateContent>

      <PrivateContent roles={Roles.SEMANTIQ}>
        <PuzzleNav.Item
          icon={<Icons.Semantiq />}
          title={t('components:SEMANTIQ')}
          itemKey='semantiq'
          path='/semantiq'
          dataTestId='semantiq-category'>
          <PuzzleNav.ItemSubLink title={t('common:DASHBOARD')} to='/semantiq/dashboard' dataTestId='dashboard-link-semantiq' />
          <PuzzleNav.ItemSubLink
            title={t('common:CUSTOM_AUDIENCES_TITLE')}
            to='/semantiq/custom-audiences'
            dataTestId='custom-audiences-link-semantiq'
          />
          <PuzzleNav.ItemSubLink
            title={t('common:IAB_AUDIENCES_TITLE')}
            to='/semantiq/iab-audiences'
            dataTestId='iab-audiences-link-semantiq'
          />
          <PuzzleNav.ItemSubLink title={t('common:DOMAINS_TITLE')} to='/semantiq/domains' dataTestId='domains-link-semantiq' />
        </PuzzleNav.Item>
      </PrivateContent>

      <PrivateContent roles={or(Roles.ADMIN, and(Roles.PUBLISHER, not(Roles.POSTNETZ_BRANCH_USER)))}>
        <PuzzleNav.Item
          dataTestId='publishers-category'
          icon={<Icons.RSS />}
          title={t('components:PUBLISHERS')}
          itemKey='publishers'
          path='/publishers'>
          <PrivateContent roles={Roles.PUBLISHER}>
            <>
              <PuzzleNav.ItemSubLink title={t('components:PUBLISHER_OVERVIEW')} to='/publishers/overview' dataTestId='overview-link' />
              <PuzzleNav.ItemSubLink
                title={t('components:PUBLISHER_CAMPAIGNS')}
                to='/publishers/campaigns'
                dataTestId='campaigns-link-publishers'
              />
              <PuzzleNav.ItemSubLink
                title={t('components:PUBLISHER_PAYOUTS')}
                to='/publishers/payouts'
                dataTestId='payouts-link-publishers'
              />
              <PuzzleNav.ItemSubLink
                title={t('components:EXCLUSIONS')}
                to='/publishers/exclusions'
                dataTestId='exclusions-link-publishers'
              />
              <PrivateContent roles={Roles.ADMIN}>
                <PuzzleNav.ItemSubLink title={t('components:TRAFFIC')} to='/publishers/traffic' dataTestId='traffic-link-publishers' />
              </PrivateContent>
            </>
          </PrivateContent>
          <PrivateContent roles={Roles.CONFIGURE_ADS_TXT}>
            <PuzzleNav.ItemSubLink title={t('components:ADS_CONFIG')} to='/publishers/ads-config' dataTestId='ads-config-link' />
          </PrivateContent>
          {DEV_MODE && (
            <PrivateContent roles={Roles.ADMIN}>
              <PuzzleNav.ItemSubLink
                isNew
                title={t('components:ADS_CONFIG_TITLE')}
                to='/publishers/ads-config-v2/configs'
                dataTestId='ads-config-link-v2'
              />
            </PrivateContent>
          )}
        </PuzzleNav.Item>
      </PrivateContent>

      <PrivateContent roles={Roles.ADMIN}>
        <PuzzleNav.Item
          dataTestId='gen-ai-link'
          icon={<Icons.AiAssistant />}
          title={t('components:GEN_AI')}
          itemKey='gen-ai'
          path={ROUTER['/gen-ai'].path.pattern}>
          <PuzzleNav.ItemSubLink
            title={t('components:GEN_AI_CHAT')}
            to={ROUTER['/gen-ai/chat'].path.pattern}
            dataTestId='gen-ai-link-chat'
          />
          <PuzzleNav.ItemSubLink
            title={t('components:GEN_AI_IMAGE')}
            to={ROUTER['/gen-ai/image'].path.pattern}
            dataTestId='gen-ai-link-image'
          />
          {DEV_MODE && (
            <PuzzleNav.ItemSubLink
              title={t('components:GEN_AI_VIDEO')}
              to={ROUTER['/gen-ai/video'].path.pattern}
              dataTestId='gen-ai-link-video'
            />
          )}
        </PuzzleNav.Item>
      </PrivateContent>

      <PrivateContent roles={Roles.ADMIN}>
        <PuzzleNav.Item
          dataTestId='ad-tags-category'
          icon={<Icons.Tag />}
          title={t('common:TITLE_AD_TAGS')}
          itemKey='ad-tags'
          path='/ad-tags'>
          <PuzzleNav.ItemSubLink title={t('components:FIRST_PARTY_WEBSITE')} to='/ad-tags/1st-party' dataTestId='1st-party-link' />
          <PuzzleNav.ItemSubLink title={t('components:THIRD_PARTY_WEBSITE')} to='/ad-tags/3rd-party' dataTestId='3rd-party-link' />
        </PuzzleNav.Item>
      </PrivateContent>

      <PrivateContent roles={or(Roles.ADMIN, Roles.HEADER_TAG)}>
        <PuzzleNav.Item
          dataTestId='header-tag-category'
          icon={<Icons.Tag />}
          title={t('components:HEADER_TAG')}
          itemKey='header-tag'
          path={ROUTER['/header-tag'].path.pattern}>
          <PuzzleNav.ItemSubLink title={t('common:TAGS')} to={ROUTER['/header-tag/tags'].path.pattern} dataTestId='header-tag-tags-link' />
          <PuzzleNav.ItemSubLink
            title={t('components:RAW_SCRIPTS')}
            to={ROUTER['/header-tag/raw-scripts'].path.pattern}
            dataTestId='header-tag-raw-scripts-link'
          />
          <PuzzleNav.ItemSubLink
            title={t('components:FLOOR_RULES')}
            to={ROUTER['/header-tag/floor-rules'].path.pattern}
            dataTestId='header-tag-floor-rules-link'
          />
          <PuzzleNav.ItemSubLink
            title={t('components:BIDDERS_CONFIGS')}
            to={ROUTER['/header-tag/bidders-configs'].path.pattern}
            dataTestId='header-tag-bidders-configs-link'
          />
        </PuzzleNav.Item>
      </PrivateContent>

      <PrivateContent roles={or(Roles.ADMIN, Roles.HEADER_TAG)}>
        <PuzzleNav.Item
          dataTestId='app-config-category'
          icon={<Icons.Development />}
          title={t('components:APP_CONFIG_TITLE')}
          itemKey='app-config'
          path={ROUTER['/app-config'].path.pattern}>
          <PuzzleNav.ItemSubLink
            title={t('common:PUBLISHERS')}
            to={ROUTER['/app-config/publishers'].path.pattern}
            dataTestId='app-config-publishers-link'
          />
        </PuzzleNav.Item>
      </PrivateContent>

      <PrivateContent roles={Roles.YALEO}>
        <PuzzleNav.Item icon={<Icons.Yaleo />} title={t('common:YALEO')} itemKey='yaleo' path='/yaleo' dataTestId='yaleo-category'>
          <PrivateContent roles={Roles.ADMIN}>
            <PuzzleNav.ItemSubLink
              title={t('components:CRAWLER_CONFIGS')}
              to='/yaleo/crawler-configuration'
              dataTestId='yaleo-crawler-configuration-link'
            />
          </PrivateContent>
          <PuzzleNav.ItemSubLink title={t('common:PLACEMENTS')} to='/yaleo/placements' dataTestId='yaleo-placements-link' />
          {DEV_MODE && (
            <PrivateContent roles={Roles.ADMIN}>
              <PuzzleNav.ItemSubLink isNew title={t('common:PLACEMENTS')} to='/yaleo/placements-v2' dataTestId='yaleo-placements-link-v2' />
            </PrivateContent>
          )}
          <PuzzleNav.ItemSubLink
            title={t('components:BTA_FEED')}
            to={ROUTER['/yaleo/bta-feed/websites'].path.pattern}
            dataTestId='bta-feed-link'
          />
          {DEV_MODE && (
            <PrivateContent roles={Roles.ADMIN}>
              <PuzzleNav.ItemSubLink
                isNew
                title={t('components:BTA_FEED')}
                to={ROUTER['/yaleo/bta-feed/v2'].path.pattern}
                dataTestId='bta-feed-link-v2'
              />
            </PrivateContent>
          )}
          <PrivateContent roles={Roles.ADMIN}>
            <PuzzleNav.ItemSubLink title={t('common:COLLECT')} to='/yaleo/collect' dataTestId='collect-link' />
            <PuzzleNav.ItemSubLink
              title={t('components:WEBSITES_ALGORITHMS_CONFIG')}
              to='/yaleo/algorithms-config'
              dataTestId='algorithms-config-link'
            />
          </PrivateContent>
        </PuzzleNav.Item>
      </PrivateContent>

      {DEV_MODE && (
        <PrivateContent roles={Roles.ADMIN}>
          <PuzzleNav.Item
            icon={<Icons.Fire />}
            title={t('common:SELF_BOOKING')}
            itemKey='self-booking'
            path='/self-booking'
            dataTestId='self-booking-category'
            isNew>
            <PuzzleNav.ItemSubLink title={t('common:CAMPAIGNS')} to='/self-booking/campaigns' dataTestId='self-booking-campaigns-link' />
            <PuzzleNav.ItemSubLink
              title={t('common:ADVERTISERS')}
              to='/self-booking/advertisers'
              dataTestId='self-booking-advertisers-link'
            />
            <PuzzleNav.ItemSubLink title={t('common:CREATIVES')} to='/self-booking/creatives' dataTestId='self-booking-creatives-link' />
            <PuzzleNav.ItemSubLink
              title={t('common:CREATIVE_APPROVAL')}
              to='/self-booking/creatives-approval'
              dataTestId='self-booking-creatives-approval-link'
            />
            <PuzzleNav.ItemSubLink
              title={t('components:PN_SCREENSHOT_MANAGEMENT')}
              to='/self-booking/screenshot-management'
              dataTestId='self-booking-screenshot-management-link'
            />
          </PuzzleNav.Item>
        </PrivateContent>
      )}

      <PrivateContent roles={or(Roles.ADMIN, Roles.CONTEXTUAL)}>
        <PuzzleNav.Item
          icon={<Icons.Database />}
          title={t('components:DMP')}
          itemKey='dmp'
          path='/targeting'
          dataTestId='dmp-targeting-category'>
          <PrivateContent roles={Roles.CONTEXTUAL}>
            <PuzzleNav.ItemSubLink title={t('common:TARGETING_TITLE')} to='/targeting/keywords' dataTestId='contextual-link' />
          </PrivateContent>
          <PrivateContent roles={Roles.ADMIN}>
            <>
              <PuzzleNav.ItemSubLink title={t('common:SEGMENTS_TITLE')} to='/targeting/segments' dataTestId='segments-link' />
              <PuzzleNav.ItemSubLink
                title={t('common:VISITOR_SEGMENTS_TITLE')}
                to='/targeting/visitor-segments'
                dataTestId='visitor-segments-link'
              />
            </>
          </PrivateContent>
        </PuzzleNav.Item>
      </PrivateContent>

      <PrivateContent roles={Roles.ADMIN}>
        <PuzzleNav.Item
          icon={<Icons.AdConsole size={16} />}
          title={t('components:ADMIN_TITLE')}
          itemKey='admin'
          path='/admin'
          dataTestId='admin-category'>
          <PuzzleNav.ItemSubLink title={t('common:ADMIN_USERS')} to='/admin/users' dataTestId='users-link' />
          <PuzzleNav.ItemSubLink title={t('common:COMPANIES')} to='/admin/companies' dataTestId='companies-link' />
          <PuzzleNav.ItemSubLink title={t('common:EMAILS')} to='/admin/mail' dataTestId='admin-mail-link' />
          <PuzzleNav.ItemSubLink title={t('common:NOTIFICATIONS')} to='/admin/notifications' dataTestId='notifications-link' />
          <PuzzleNav.ItemSubLink
            isNew
            title={t('common:WEBSITE_MAPPINGS')}
            to={ROUTER['/admin/website-mapping'].path.pattern}
            dataTestId='website-mappings-link'
          />
          <PuzzleNav.ItemSubLink title={t('components:LOCALIZATION')} to='/admin/localization' dataTestId='localization-link-v2' />
          <PuzzleNav.ItemSubLink title={t('components:ADMIN_BATCH')} to='/admin/batch' dataTestId='admin-batch-link' />
          <PuzzleNav.ItemSubLink title={`${t('components:ADMIN_BATCH')}-k8s`} to='/admin/batch-k8s' dataTestId='admin-batch-k8s-link' />
          <PuzzleNav.ItemSubLink
            title={t('components:XANDR_CREDENTIALS')}
            to='/admin/xandr-credentials'
            dataTestId='xandr-credentials-link'
          />
          <PuzzleNav.ItemSubLink title={t('components:XANDR_SEGMENTS')} to='/admin/xandr-segments' dataTestId='xandr-segments-link' />
          <PuzzleNav.ItemSubLink dataTestId='permalead-link' title={t('common:PERMALEAD')} to='/admin/permalead' />
        </PuzzleNav.Item>
      </PrivateContent>

      <PrivateContent roles={Roles.ACCESS_COMPLIANCE}>
        <PuzzleNav.ItemLink
          icon={<Icons.Compliance />}
          title={t('common:ADMIN_COMPLIANCE')}
          path='/compliance'
          to='/compliance'
          dataTestId='compliance'
        />
      </PrivateContent>

      {DEV_MODE && (
        <PrivateContent roles={Roles.SUPPORT_CENTER}>
          <PuzzleNav.Item
            icon={<Icons.Headphones />}
            title={t('admin:SUPPORT_CENTER')}
            itemKey='support-center'
            path={ROUTER['/support-center'].path.pattern}
            isNew
            dataTestId='support-center'>
            <PuzzleNav.ItemSubLink
              title={t('components:TICKETS')}
              to={ROUTER['/support-center/tickets'].path.pattern}
              dataTestId='feedback'
            />
            <PuzzleNav.ItemSubLink title={t('common:USERSNAP')} onClick={openUserSnap} dataTestId='feedback' />
          </PuzzleNav.Item>
        </PrivateContent>
      )}

      <PrivateContent roles={settingsRouteRoles}>
        <PuzzleNav.Item
          icon={<Icons.Gear />}
          title={t('common:BUTTON_SETTINGS')}
          itemKey='settings'
          path='/settings'
          dataTestId='settings-category'>
          <PrivateContent roles={and(Roles.MANAGE_ACCOUNTS, not(Roles.POSTADMANAGER))}>
            <PuzzleNav.ItemSubLink title={t('common:DATA_IMPORT')} to='/settings/data-import' dataTestId='data-import-link' />
          </PrivateContent>
          <PrivateContent roles={Roles.POSTNETZ}>
            <PuzzleNav.ItemSubLink
              title={t('common:PARTNER_MANAGEMENT_SHORT')}
              to='/settings/partner-filial-management'
              dataTestId='partner-filial-link'
            />
          </PrivateContent>
          <PrivateContent roles={Roles.POSTNETZ}>
            <PuzzleNav.ItemSubLink
              title={t('components:ANALOG_PRODUCTS')}
              to='/settings/analog-products'
              dataTestId='analog-products-link'
            />
          </PrivateContent>
          <PrivateContent roles={Roles.MANAGE_DIGITAL_PRODUCTS}>
            <PuzzleNav.ItemSubLink
              title={t('common:DIGITAL_PRODUCTS')}
              to='/settings/digital-products'
              dataTestId='digital-products-link'
            />
          </PrivateContent>
          <PrivateContent roles={Roles.MANAGE_RADIO_PRODUCTS}>
            <PuzzleNav.ItemSubLink title={t('common:RADIO_PRODUCTS')} to='/settings/radio-products' dataTestId='radio-products-link' />
          </PrivateContent>
          <PrivateContent roles={Roles.MANAGE_TV_PRODUCTS}>
            <PuzzleNav.ItemSubLink title={t('common:MANAGE_TV_PRODUCTS')} to='/settings/tv-products' dataTestId='tv-products-link' />
          </PrivateContent>
          <PrivateContent roles={Roles.MANAGE_PRINT_PRODUCTS}>
            <PuzzleNav.ItemSubLink title={t('common:PRINT_PRODUCTS')} to='/settings/print-products' dataTestId='print-products-link' />
            {DEV_MODE && (
              <PuzzleNav.ItemSubLink
                isNew
                title={t('common:PRINT_PRODUCTS')}
                to='/settings/print-products2'
                dataTestId='print-products-link2'
              />
            )}
          </PrivateContent>
          <PrivateContent roles={Roles.MANAGE_POS_PRODUCTS}>
            <PuzzleNav.ItemSubLink
              title={t('common:POINT_OF_SALE_PRODUCTS')}
              to='/settings/point-of-sale-products'
              dataTestId='point-of-sale-products-link'
            />
          </PrivateContent>
          <PrivateContent roles={or(Roles.MANAGE_DIGITAL_PRODUCTS, Roles.MANAGE_PRINT_PRODUCTS, Roles.MANAGE_POS_PRODUCTS)}>
            <PuzzleNav.ItemSubLink title={t('common:POSITION_GROUPS')} to='/settings/position-groups' dataTestId='position-groups-link' />
            {DEV_MODE && (
              <PuzzleNav.ItemSubLink
                isNew
                title={t('common:POSITION_GROUPS')}
                to={ROUTER['/settings/position-groups-v2'].path.pattern}
                dataTestId='position-groups-link-v2'
              />
            )}
          </PrivateContent>
          <PrivateContent
            roles={or(
              Roles.MANAGE_DIGITAL_PRODUCTS,
              Roles.MANAGE_PRINT_PRODUCTS,
              Roles.MANAGE_TV_PRODUCTS,
              Roles.MANAGE_RADIO_PRODUCTS,
              Roles.MANAGE_POS_PRODUCTS,
            )}>
            <PuzzleNav.ItemSubLink title={t('common:PRICE_LISTS')} to='/settings/price-lists' dataTestId='price-lists-link' />
          </PrivateContent>
          <PrivateContent roles={Roles.BOOK_CAMPAIGNS}>
            <PuzzleNav.ItemSubLink title={t('common:SALES_UNITS')} to='/settings/sales-units' dataTestId='sales-units-link' />
          </PrivateContent>
          <PrivateContent roles={Roles.PAYOUT}>
            <PuzzleNav.ItemSubLink title={t('common:WEBSITES')} to='/settings/websites' dataTestId='websites-link' />
          </PrivateContent>
          <PrivateContent roles={Roles.ADMIN}>
            <PuzzleNav.ItemSubLink title={t('common:BRANDS_TITLE')} to='/settings/brands' dataTestId='brands-link' />
          </PrivateContent>
          <PrivateContent roles={or(Roles.ADMIN, Roles.PAYOUT)}>
            <>
              <PuzzleNav.ItemSubLink
                title={t('components:TITLE_XANDR_MANAGEMENT')}
                to='/settings/xandr-programmatic'
                path='/settings/xandr-programmatic'
                dataTestId='xandr-programmatic-link'
              />
              <PuzzleNav.ItemSubLink
                title={t('components:TITLE_GOOGLE_MANAGEMENT')}
                to='/settings/google-programmatic'
                path='/settings/google-programmatic'
                dataTestId='google-programmatic-link'
              />
              <PuzzleNav.ItemSubLink
                title={t('components:TITLE_ADOMIK_MANAGEMENT')}
                to='/settings/adomik-programmatic'
                path='/settings/adomik-programmatic'
                dataTestId='adomik-programmatic-link'
              />
            </>
          </PrivateContent>
          <PrivateContent roles={Roles.ADMIN}>
            <>
              <PuzzleNav.ItemSubLink
                title={t('components:TITLE_PASSENDO')}
                to='/settings/passendo/websites-mapping'
                dataTestId='passendo-link'
              />
              <PuzzleNav.ItemSubLink
                title={t('components:TITLE_ADSENSE_MAPPINGS')}
                to='/settings/adsense/mappings'
                path='/settings/adsense'
                dataTestId='ad-sense-mappings-link'
              />
            </>
          </PrivateContent>
          <PrivateContent roles={or(Roles.BOOK_CAMPAIGNS, Roles.POSTADMANAGER)}>
            <PuzzleNav.ItemSubLink
              title={t('components:COMPANY_SETTINGS')}
              to={ROUTER['/settings/company-settings'].path.pattern}
              dataTestId='company-settings-link'
            />
          </PrivateContent>
          <PrivateContent roles={or(Roles.MANAGE_ACCOUNTS, Roles.POSTADMANAGER)}>
            <PuzzleNav.ItemSubLink title={t('common:INDUSTRIES')} to='/settings/industries' dataTestId='industries-link' />
          </PrivateContent>
          <PrivateContent roles={Roles.MANAGE_CAMPAIGN_TYPES}>
            <PuzzleNav.ItemSubLink title={t('components:CAMPAIGN_TYPES')} to='/settings/campaign-types' dataTestId='campaign-types-link' />
          </PrivateContent>
          <PrivateContent roles={and(Roles.REPORT_BUILDER, or(Roles.BOOK_CAMPAIGNS, Roles.SALESFUNNEL, Roles.POSTADMANAGER))}>
            <PuzzleNav.ItemSubLink title={t('common:LABELS')} to='/settings/labels' dataTestId='admin-labels-link' />
          </PrivateContent>
          <PrivateContent roles={Roles.MANAGE_REFERENCE_PER_AD_MEDIA}>
            <PuzzleNav.ItemSubLink
              title={t('common:REFERENCE_PER_ADMEDIA')}
              to='/settings/reference-per-admedia'
              dataTestId='reference-per-admedia-link'
            />
          </PrivateContent>
          <PrivateContent roles={Roles.SPECIAL_DISCOUNTS}>
            <PuzzleNav.ItemSubLink
              title={t('common:SPECIAL_DISCOUNTS')}
              to='/settings/special-discounts'
              dataTestId='special-discounts-link'
              isNew
            />
          </PrivateContent>
        </PuzzleNav.Item>
      </PrivateContent>
    </PuzzleNav>
  );
});
